import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Clock, CheckCircle, XCircle, ArrowRight, ArrowLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";

interface Question {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number | number[];
}

interface Quiz {
  id: string;
  title: string;
  description: string;
  questions: Question[];
  passingScore: number;
  moduleId: string;
}

export default function Quiz() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number[]>>({});
  const [timeLeft, setTimeLeft] = useState(1800); // 30 minutes default
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [score, setScore] = useState<number | null>(null);

  const { data: quizData, isLoading } = useQuery({
    queryKey: ['/quizzes', (params as any)?.id],
    enabled: !!(params as any)?.id,
  });

  const { data: previousAttempt } = useQuery({
    queryKey: ['/quiz-attempts', (params as any)?.id],
    enabled: !!(params as any)?.id,
  });

  const submitQuizMutation = useMutation({
    mutationFn: async (data: { quizId: string; answers: Record<string, number>; score: number; isPassed: boolean }) => {
      const response = await apiRequest("POST", "/quiz-attempts", data);
      return response.json();
    },
    onSuccess: async (result) => {
      setScore(result.attempt.score);
      setIsSubmitted(true);
      
      // Explicitly mark module as completed regardless of score
      if (quiz?.moduleId) {
        try {
          await apiRequest("POST", "/progress", {
            moduleId: parseInt(quiz.moduleId),
            isCompleted: true,
            timeSpent: 300, // 5 minutes for quiz completion
          });
        } catch (error) {
          console.error("Failed to mark module as completed:", error);
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['/quiz-attempts'] });
      queryClient.invalidateQueries({ queryKey: ['/progress'] });
      queryClient.invalidateQueries({ queryKey: ['/progress/overall'] });
      
      toast({
        title: "Quiz Completed!",
        description: `You scored ${result.attempt.score}% on the quiz. Great job completing it!`,
        variant: "default",
      });
    },
  });

  // Timer effect
  useEffect(() => {
    if (isSubmitted || !(quizData as any)?.quiz) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isSubmitted, quizData]);

  const quiz: Quiz | undefined = (quizData as any)?.quiz;
  const currentQuestion = quiz?.questions?.[currentQuestionIndex];

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleAnswerToggle = (questionId: string, answerIndex: number) => {
    setAnswers(prev => {
      const currentAnswers = prev[questionId] || [];
      const newAnswers = currentAnswers.includes(answerIndex)
        ? currentAnswers.filter(idx => idx !== answerIndex)
        : [...currentAnswers, answerIndex];
      
      return {
        ...prev,
        [questionId]: newAnswers
      };
    });
  };

  const isAnswerSelected = (questionId: string, answerIndex: number) => {
    return answers[questionId]?.includes(answerIndex) || false;
  };

  const calculateScore = () => {
    if (!quiz?.questions) return 0;
    
    let correct = 0;
    quiz.questions.forEach((question) => {
      const userAnswers = answers[question.id] || [];
      const correctAnswers = Array.isArray(question.correctAnswer) 
        ? question.correctAnswer 
        : [question.correctAnswer];
      
      // Check for exact match (same length and same elements)
      const sortedUserAnswers = [...userAnswers].sort((a, b) => a - b);
      const sortedCorrectAnswers = [...correctAnswers].sort((a, b) => a - b);
      
      const isCorrect = sortedUserAnswers.length === sortedCorrectAnswers.length &&
        sortedUserAnswers.every((answer, idx) => answer === sortedCorrectAnswers[idx]);
      
      if (isCorrect) {
        correct++;
      }
    });
    
    return Math.round((correct / quiz.questions.length) * 100);
  };

  const handleSubmitQuiz = () => {
    const finalScore = calculateScore();
    const isPassed = true; // Always consider as passed since we complete modules regardless of score
    
    submitQuizMutation.mutate({
      quizId: quiz?.id || '',
      answers,
      score: finalScore,
      isPassed,
    });
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < (quiz?.questions?.length || 0) - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const progressPercentage = quiz ? ((currentQuestionIndex + 1) / quiz.questions.length) * 100 : 0;
  const answeredQuestions = Object.keys(answers).length;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="quiz-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servify-purple mx-auto mb-4"></div>
          <p className="text-gray-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="quiz-not-found">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Quiz Not Found</h2>
            <p className="text-gray-600 mb-4">The requested quiz could not be found.</p>
            <Button onClick={() => setLocation('/')} data-testid="button-go-home">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="quiz-results">
        <Card className="w-full max-w-2xl">
          <CardContent className="pt-6 text-center">
            <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 bg-servify-green text-white">
              <CheckCircle className="h-12 w-12" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-2" data-testid="text-quiz-result-title">
              Quiz Completed!
            </h2>
            
            <p className="text-xl text-gray-600 mb-6" data-testid="text-quiz-score">
              You scored {score}% on {quiz.title}. Great job completing the quiz!
            </p>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-servify-purple" data-testid="text-questions-answered">
                    {answeredQuestions}
                  </p>
                  <p className="text-sm text-gray-600">Questions Answered</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-servify-green" data-testid="text-correct-answers">
                    {Math.round((score || 0) / 100 * quiz.questions.length)}
                  </p>
                  <p className="text-sm text-gray-600">Correct Answers</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900" data-testid="text-passing-score">
                    {quiz.passingScore}%
                  </p>
                  <p className="text-sm text-gray-600">Passing Score</p>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-4 justify-center">
              <Button 
                onClick={() => setLocation('/training')}
                className="bg-servify-purple hover:bg-servify-purple-dark"
                data-testid="button-back-to-training"
              >
                Back to Training
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="quiz-page">
      {/* Quiz Header */}
      <Card>
        <CardHeader className="border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl" data-testid="text-quiz-title">{quiz.title}</CardTitle>
              <p className="text-gray-600 mt-1" data-testid="text-quiz-description">{quiz.description}</p>
            </div>
            <div className="text-right">
              <div className="flex items-center text-servify-purple font-semibold text-lg" data-testid="text-time-remaining">
                <Clock className="h-5 w-5 mr-2" />
                {formatTime(timeLeft)}
              </div>
              <p className="text-sm text-gray-500">Time Remaining</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-gray-700" data-testid="text-question-progress">
              Question {currentQuestionIndex + 1} of {quiz.questions.length}
            </span>
            <span className="text-sm font-medium text-gray-700" data-testid="text-answered-count">
              {answeredQuestions} answered
            </span>
          </div>
          <Progress value={progressPercentage} className="w-full" data-testid="progress-quiz" />
        </CardContent>
      </Card>

      {/* Current Question */}
      <Card>
        <CardContent className="pt-6">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4" data-testid={`text-question-${currentQuestion?.id}`}>
              {currentQuestion?.question}
            </h3>
            
            <div className="space-y-3">
              {(() => {
                const correctAnswers = Array.isArray(currentQuestion?.correctAnswer) 
                  ? currentQuestion.correctAnswer 
                  : [currentQuestion?.correctAnswer];
                const isMultiple = correctAnswers.length > 1;
                
                return (
                  <>
                    {isMultiple && (
                      <p className="text-sm text-gray-500 mb-3">
                        Select all correct answers (multiple selections allowed)
                      </p>
                    )}
                    {currentQuestion?.options.map((option, index) => (
                      <div 
                        key={index} 
                        className="flex items-center space-x-2 p-3 border-2 rounded-lg transition-all cursor-pointer hover:bg-gray-50"
                        style={{
                          borderColor: isAnswerSelected((currentQuestion as any)?.id, index) 
                            ? '#7C3AED' 
                            : '#E5E7EB'
                        }}
                        onClick={() => handleAnswerToggle((currentQuestion as any)?.id, index)}
                      >
                        <Checkbox
                          checked={isAnswerSelected((currentQuestion as any)?.id, index)}
                          onCheckedChange={() => handleAnswerToggle((currentQuestion as any)?.id, index)}
                          id={`option-${index}`}
                          data-testid={`checkbox-option-${currentQuestion.id}-${index}`}
                          className="text-servify-purple focus:ring-servify-purple"
                        />
                        <Label 
                          htmlFor={`option-${index}`} 
                          className="flex-1 cursor-pointer"
                          data-testid={`label-option-${currentQuestion.id}-${index}`}
                        >
                          {option}
                        </Label>
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          </div>
          
          {/* Navigation Buttons */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={goToPreviousQuestion}
              disabled={currentQuestionIndex === 0}
              data-testid="button-previous-question"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>
            
            <div className="flex space-x-3">
              {currentQuestionIndex === quiz.questions.length - 1 ? (
                <Button
                  onClick={handleSubmitQuiz}
                  disabled={answeredQuestions < quiz.questions.length || submitQuizMutation.isPending}
                  className="bg-servify-green hover:bg-servify-green-light"
                  data-testid="button-submit-quiz"
                >
                  {submitQuizMutation.isPending ? "Submitting..." : "Submit Quiz"}
                </Button>
              ) : (
                <Button
                  onClick={goToNextQuestion}
                  disabled={currentQuestionIndex === quiz.questions.length - 1}
                  className="bg-servify-purple hover:bg-servify-purple-dark"
                  data-testid="button-next-question"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Question Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Question Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2" data-testid="question-overview">
            {quiz.questions.map((question, index) => (
              <button
                key={question.id}
                onClick={() => setCurrentQuestionIndex(index)}
                className={`w-10 h-10 rounded-lg font-medium text-sm transition-colors ${
                  index === currentQuestionIndex
                    ? 'bg-servify-purple text-white'
                    : answers[question.id] !== undefined
                      ? 'bg-servify-green text-white'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                data-testid={`button-question-${index + 1}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <div className="flex items-center space-x-6 mt-4 text-sm">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-servify-purple rounded mr-2"></div>
              <span>Current</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-servify-green rounded mr-2"></div>
              <span>Answered</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-200 rounded mr-2"></div>
              <span>Unanswered</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
